import React, { useEffect, useState } from "react";
import {
    AccountCircle as AccountIcon,
    Lock as LockIcon,
    Login as LoginIcon,
    Star as StarIcon
} from "@mui/icons-material";
import Logger from "../../services/Logger";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Paper,
    Tooltip,
    Typography
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SURFACE } from "../../dark-theme/palette";
import AuthService from "../../services/AuthService";
import UserService from "../../services/UserService";
import UtilService from "../../services/UtilService";
import SubscriptionService from "../../services/SubscriptionService";
import { usePagesTranslation } from "../locales";
import {
    SUBSCRIPTION_PLANS,
    PremiumPlanFeaturesSection,
    PlanPickerFootnotes,
    getSubscriptionPlanPickerEntries,
    getSubscriptionPlanLabel,
    PlanCardOfferExtras,
} from "./PanelPlan";
import { trackEvent } from "../../services/GoogleAnalytics";
import StripeClimateBadgeIframe from "../components/StripeClimateBadgeIframe";

const SubscriptionPage = () => {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const [, setUser] = useState(null);
    /** No card selected until the user picks one; Upgrade appears only for paid plans. */
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [showLoginPrompt, setShowLoginPrompt] = useState(false);
    const [userSubscription, setUserSubscription] = useState('free');
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        trackEvent("entered", { name: "SubscriptionPage" });
    }, []);

    useEffect(() => {
        const onPageShow = (event) => {
            if (event.persisted) {
                setIsProcessing(false);
            }
        };
        window.addEventListener("pageshow", onPageShow);
        return () => window.removeEventListener("pageshow", onPageShow);
    }, []);

    useEffect(() => {
        const getUser = async () => {
            try {
                const isAuthenticated = await AuthService.isAuthenticated();
                setIsAuthenticated(isAuthenticated);
                if (isAuthenticated) {
                    const userInfo = await AuthService.getUser();
                    setUser(userInfo);
                    // Get user data to determine subscription status
                    const userData = await UserService.getUserInfo();
                    if (userData?.cloudUser) {
                        if (UtilService.isStandardPayPlanForUser(userData.cloudUser) || userData.cloudUser.userRole === 'ADMIN') {
                            setUserSubscription('premium');
                        } else {
                            setUserSubscription('free');
                        }
                    }
                } else {
                    setUserSubscription('free');
                }
            } catch (error) {
                if (error.name !== 'UserUnAuthenticatedException') {
                    Logger.error("Error getting user:", error);
                }
                setIsAuthenticated(false);
                setUserSubscription('free');
            }
        };

        getUser();
    }, []);

    const planEntries = getSubscriptionPlanPickerEntries({ hideMultiMonthPlans: true });
    const planPickerKeys = planEntries.map(([key]) => key);

    useEffect(() => {
        if (selectedPlan != null && !planPickerKeys.includes(selectedPlan)) {
            setSelectedPlan(null);
        }
    }, [planPickerKeys, selectedPlan]);

    const selectedPlanData = selectedPlan != null ? SUBSCRIPTION_PLANS[selectedPlan] : null;
    const canCheckout =
        Boolean(selectedPlanData?.productKey && selectedPlanData?.priceKey);

    const handleSubscribe = async () => {
        if (!canCheckout || selectedPlan == null) return;
        setIsProcessing(true);
        setError('');
        try {
            const result = await SubscriptionService.handleSubscribe(selectedPlan, SUBSCRIPTION_PLANS);

            if (result.requiresLogin) {
                setShowLoginPrompt(true);
                return;
            }

            if (!result.success) {
                setError(result.error || 'Subscription failed. Please try again.');
                return;
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const handleLogin = () => {
        navigate('/login');
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <StarIcon sx={{ color: '#ffd700', fontSize: 32 }} />
                    <Typography variant="h4" component="h1">
                        {t("subs.dialog.title")}
                    </Typography>
                </Box>

                {/* Error Message */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {/* Plan Selection */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        {t("subs.dialog.trigger-general")}
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                        {t("subs.dialog.choose-plan")}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, pt: 2, flexWrap: 'wrap' }}>
                        {planEntries.map(([key, plan]) => (
                            <Card
                                key={key}
                                sx={{
                                    flex: {
                                        xs: '1 1 100%',
                                        sm: '1 1 calc(50% - 8px)',
                                        md: '1 1 calc(25% - 12px)'
                                    },
                                    minWidth: { md: 0 },
                                    cursor: 'pointer',
                                    border:
                                        selectedPlan != null && selectedPlan === key
                                            ? '2px solid #3BA666'
                                            : `1px solid ${SURFACE.border}`,
                                    position: 'relative',
                                    overflow: 'visible',
                                    '&:hover': {
                                        borderColor: '#3BA666'
                                    }
                                }}
                                onClick={() => setSelectedPlan(key)}
                            >
                                {plan.popular && (
                                    <Chip
                                        label={t("subs.dialog.most-popular")}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            top: -12,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            backgroundColor: '#3BA666',
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            whiteSpace: 'nowrap',
                                            zIndex: 1
                                        }}
                                    />
                                )}
                                <CardContent sx={{ textAlign: 'center', pt: plan.popular ? 3 : 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        {getSubscriptionPlanLabel(t, key)}
                                    </Typography>
                                    <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                                        {plan.price}
                                    </Typography>
                                    <PlanCardOfferExtras planKey={key} plan={plan} />
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                    <PlanPickerFootnotes planKeys={planPickerKeys} />
                </Box>

                {/* Features List */}
                <PremiumPlanFeaturesSection />

                {/* Current Plan Info */}
                {isAuthenticated && userSubscription === 'free' && (
                    <Box sx={{
                        p: 2,
                        backgroundColor: SURFACE.sunken,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 3
                    }}>
                        <LockIcon color="action" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">
                            {t("subs.dialog.free-plan-msg")}
                        </Typography>
                    </Box>
                )}

                <StripeClimateBadgeIframe sx={{ mt: 3 }} />

                <Divider sx={{ my: 3 }} />

                {/* Action Buttons — sticky at bottom of viewport once user scrolls; Upgrade only for paid selection */}
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        justifyContent: 'flex-end',
                        flexWrap: 'wrap',
                        mx: -4,
                        px: 4,
                        pt: 2,
                        pb: 3,
                        mt: 1,
                        bgcolor: 'background.paper',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 -6px 16px rgba(0,0,0,0.06)'
                    }}
                >
                    {/* Login Prompt */}
                    {showLoginPrompt && (
                        <Box
                            sx={{
                                mb: 3,
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: SURFACE.card,
                                border: '2px solid',
                                borderColor: '#3BA666'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <AccountIcon sx={{ color: 'info.dark' }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1" fontWeight="bold" sx={{ color: 'info.dark', mb: 0.5 }}>
                                        {t("subs.dialog.login-required")}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'info.dark' }}>
                                        {t("subs.dialog.login-required-desc")}
                                    </Typography>
                                </Box>
                                <Tooltip title={t("subs.dialog.login-signup")} arrow>
                                    <LoginIcon 
                                        sx={{ 
                                            color: 'info.dark',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                opacity: 0.8,
                                                transform: 'scale(1.1)',
                                                transition: 'all 0.2s ease-in-out'
                                            }
                                        }}
                                        onClick={handleLogin}
                                    />
                                </Tooltip>
                            </Box>
                        </Box>
                    )}
                    {showLoginPrompt ? (
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <Button onClick={() => navigate(-1)} variant="outlined">
                                {t("subs.dialog.cancel")}
                            </Button>
                            <Button
                                onClick={handleLogin}
                                variant="contained"
                                sx={{
                                    backgroundColor: '#3BA666',
                                    '&:hover': { backgroundColor: '#2e7d32' }
                                }}
                            >
                                {t("subs.dialog.login-signup")}
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <Button onClick={() => navigate(-1)} variant="outlined">
                                {t("subs.dialog.back")}
                            </Button>
                            {canCheckout && (
                                <Button
                                    onClick={handleSubscribe}
                                    variant="contained"
                                    disabled={isProcessing}
                                    sx={{
                                        backgroundColor: '#3BA666',
                                        '&:hover': { backgroundColor: '#2e7d32' }
                                    }}
                                >
                                    {isProcessing ? t("subs.dialog.processing") : t("subs.dialog.upgrade-now")}
                                </Button>
                            )}
                        </Box>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default SubscriptionPage;
